import React, {useEffect, useState, useCallback, useRef} from "react";
import "./ErrorDialog.css";

let _open = null;
let _close = null;

// Imperative API: any module (including non-React code paths like the
// saveScene mutation onError) can call openErrorDialog(...) without
// threading a ref through the tree. The provider component subscribes on
// mount and unsubscribes on unmount.
export function openErrorDialog(payload) {
  if (_open) _open(payload);
  else console.error("[ErrorDialog] No provider mounted, falling back to console:", payload);
}

export function closeErrorDialog() {
  if (_close) _close();
}

export const ErrorDialogProvider = () => {
  const [payload, setPayload] = useState(null);
  const [copied, setCopied] = useState(false);
  // Focus moves to the primary action on open so keyboard users land inside
  // the dialog instead of wherever they were last focused outside it.
  const primaryButtonRef = useRef(null);

  useEffect(() => {
    _open = (next) => {
      setPayload(next);
      setCopied(false);
    };
    _close = () => setPayload(null);
    return () => {
      _open = null;
      _close = null;
    };
  }, []);

  useEffect(() => {
    if (payload) primaryButtonRef.current?.focus();
  }, [payload]);

  const handleCopy = useCallback(async () => {
    if (!payload?.diagnostics) return;
    const text = JSON.stringify(payload.diagnostics, null, 2);
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API can be unavailable (insecure context, permissions).
      // Fall back to a textarea select so the user can ⌘C manually.
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch {
        // Last-resort no-op: the diagnostics are still in console.
      }
      document.body.removeChild(ta);
    }
  }, [payload]);

  if (!payload) return null;

  const {title, summary, details, diagnostics} = payload;

  return (
    <div
      className="error-dialog-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="error-dialog-title"
    >
      <div className="error-dialog">
        <h2 id="error-dialog-title" className="error-dialog-title">
          {title || "Something went wrong"}
        </h2>
        {summary && <p className="error-dialog-summary">{summary}</p>}
        {details && details.length > 0 && (
          <ul className="error-dialog-details">
            {details.map((d, i) => (
              <li key={i}>{d}</li>
            ))}
          </ul>
        )}
        <div className="error-dialog-actions">
          {diagnostics && (
            <button
              type="button"
              className="error-dialog-button error-dialog-button-secondary"
              onClick={handleCopy}
            >
              {copied ? "Copied!" : "Copy diagnostics"}
            </button>
          )}
          <button
            ref={primaryButtonRef}
            type="button"
            className="error-dialog-button error-dialog-button-primary"
            onClick={() => setPayload(null)}
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
};
